import React, { useMemo } from 'react';
import {
  ITopPathsSliceFieldsFragment,
  PathModeKey,
  QueryType,
} from '../../../utils/apollo/graphqlSchema';
import { assertCondition } from '../../../utils/types';
import { isNil } from 'lodash';
import {
  ITopPathAndSlice,
  LabelFunction,
  TopPathsGroup,
} from './TopPathsGroup';
import { ChartMode } from '../types';
import useCacheTopPathsLlmRawDataEffect from '../hooks/llmCacheRawData/useCacheTopPathsLlmRawDataEffect';
import ThinDivider from '../../ThinDivider';
import { TopPathsChartWrapper } from './TopPathsChartWrapper';
import { useChartContext } from '../ChartContext';
const baseLabelFunction: LabelFunction = (hint) =>
  hint.map((h) => h.name).join(', ');

const sliceLabelFunction: LabelFunction = (hint) =>
  `Breakdown by: ${hint.map((h) => `${h.name}: ${h.value}`).join(', ')}`;

const getTopPathWithBelongingSlice = (slice: ITopPathsSliceFieldsFragment) =>
  slice.topPaths.map<ITopPathAndSlice>((topPath) => ({
    topPath,
    belongingSlice: slice,
  }));

interface IProps {
  analysisId: string;
  mode: ChartMode;
}

const TopPathsChart = ({ analysisId, mode }: IProps) => {
  const { chartData } = useChartContext();
  assertCondition(
    chartData.queryType === QueryType.Path &&
      chartData.mode === PathModeKey.TopPaths,
  );
  const slicesTopPathAndSlice = useMemo((): ITopPathAndSlice[] => {
    const sliceTopPathAndSlice = chartData.slices.flatMap(
      getTopPathWithBelongingSlice,
    );
    sliceTopPathAndSlice.sort((a, b) => a.topPath.rank - b.topPath.rank);
    return sliceTopPathAndSlice;
  }, [chartData.slices]);

  const baseTopPathAndSlice = useMemo(
    (): ITopPathAndSlice[] =>
      !isNil(chartData.base)
        ? getTopPathWithBelongingSlice(chartData.base)
        : [],
    [chartData.base],
  );

  const showDivider =
    baseTopPathAndSlice.length > 0 && slicesTopPathAndSlice.length > 0;

  useCacheTopPathsLlmRawDataEffect({
    analysisId,
    baseTopPathAndSlice,
    slicesTopPathAndSlice,
  });

  return (
    <TopPathsChartWrapper mode={mode} analysisId={analysisId}>
      <TopPathsGroup
        topPaths={baseTopPathAndSlice}
        labelFunction={baseLabelFunction}
        mode={mode}
      />
      {showDivider && <ThinDivider layout="horizontal" size="full" />}
      <TopPathsGroup
        topPaths={slicesTopPathAndSlice}
        labelFunction={sliceLabelFunction}
        mode={mode}
      />
    </TopPathsChartWrapper>
  );
};

export default TopPathsChart;
